import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  ChannelModelRecord,
  CustomerManagerCustomerSummary,
  OrgSkuRateLimitRecord,
  PutRateLimitRequest,
  QuotaLane,
  RateLimitRefusalKind,
  RateLimitRefusalRecord,
  SkuQuotaLane,
  TaskLaneSkuDefault,
} from '@nadoutong/contracts';
import { useQuotaCustomers } from '../../api/customerOwnership.ts';
import { useChannelModelsList } from '../../api/useChannelModels.ts';
import {
  useAllOrgSkuRateLimits,
  useDeleteGlobalRateLimit,
  useGlobalRateLimit,
  useOrgAllRateLimits,
  usePutGlobalRateLimit,
  usePutQuotaBypass,
  useDeleteSkuRateLimit,
  usePutSkuRateLimit,
  useQuotaBypass,
  useRateLimitRefusals,
  useSkuRateLimits,
} from '../../api/useRoutingLoad.ts';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  Input,
  PageHeader,
  Panel,
  Switch,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  Tabs,
  TabsList,
  TabsTrigger,
} from '../../ui/index.ts';
import { buildRateLimitWrite } from '../routing-load/loadSettingsFields.ts';
import { QuotaCustomerDrawer } from './QuotaCustomerDrawer.tsx';
import { SearchDropdown } from './SearchDropdown.tsx';
import { formatCap, SourceChip } from './SourceChip.tsx';
import { TpmField } from './TpmField.tsx';
import { formatTpmCap, formatTpmMInput } from './tpmUnits.ts';
import { groupByNamespace, skuShortName, truncateId } from './skuDisplay.ts';

type EditorTab = 'global' | 'sku';
type RefusalWindow = '1h' | '24h' | '7d';

const REFUSAL_KIND_KEY: Record<RateLimitRefusalKind, I18nKey> = {
  l1_reject_minute: 'quotas.refusals.kind.l1_reject_minute',
  task_admission_reject: 'quotas.refusals.kind.task_admission_reject',
  quota_bypass_would_reject: 'quotas.refusals.kind.quota_bypass_would_reject',
  task_admission_bypass_would_reject: 'quotas.refusals.kind.task_admission_bypass_would_reject',
};

function sinceIso(window: RefusalWindow): string {
  const ms = window === '1h' ? 3_600_000 : window === '24h' ? 86_400_000 : 7 * 86_400_000;
  return new Date(Date.now() - ms).toISOString();
}

function parseCaps(
  rpmDraft: string,
  tpmDraft: string,
  originalTpm?: number | null,
): PutRateLimitRequest | null {
  const result = buildRateLimitWrite(rpmDraft, tpmDraft, originalTpm);
  if (result.kind !== 'put') return null;
  return result.body;
}

function customerLabel(
  orgId: string,
  byId: ReadonlyMap<string, CustomerManagerCustomerSummary>,
): { readonly name: string; readonly email: string } {
  const row = byId.get(orgId);
  return { name: row?.org_name ?? orgId, email: row?.email ?? '' };
}

export function QuotaCenterPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const orgId = params.get('org');
  const skuParam = params.get('sku');
  const bypass = useQuotaBypass();
  const putBypass = usePutQuotaBypass();
  const [refusalWindow, setRefusalWindow] = useState<RefusalWindow>('24h');
  const refusalsSince = useMemo(() => sinceIso(refusalWindow), [refusalWindow]);
  const refusals24hSince = useMemo(() => sinceIso('24h'), []);
  const refusals = useRateLimitRefusals(refusalsSince);
  const refusals24h = useRateLimitRefusals(refusals24hSince);
  const [editorTab, setEditorTab] = useState<EditorTab>(skuParam !== null ? 'sku' : 'global');
  const [orgQuery, setOrgQuery] = useState('');
  const [skuQuery, setSkuQuery] = useState('');
  const [orgOpen, setOrgOpen] = useState(false);
  const [skuOpen, setSkuOpen] = useState(false);
  const orgAnchorRef = useRef<HTMLDivElement | null>(null);
  const skuAnchorRef = useRef<HTMLDivElement | null>(null);
  const [flashSku, setFlashSku] = useState<string | null>(null);
  const [dedicatedFilter, setDedicatedFilter] = useState('');
  const customersQuery = useQuotaCustomers();
  const skusQuery = useChannelModelsList();
  const skuLimits = useSkuRateLimits();
  const orgAll = useOrgAllRateLimits();
  const orgSkuAll = useAllOrgSkuRateLimits();

  const customers = customersQuery.data?.customers ?? [];
  const customerById = useMemo(() => {
    const map = new Map<string, CustomerManagerCustomerSummary>();
    for (const row of customers) map.set(row.org_id, row);
    return map;
  }, [customers]);
  const catalog = skusQuery.data ?? [];
  const skuById = useMemo(() => {
    const map = new Map<string, ChannelModelRecord>();
    for (const row of catalog) map.set(row.sku_id, row);
    return map;
  }, [catalog]);
  const orgAllById = useMemo(() => {
    const map = new Map<string, { rpm: number | null; tpm: number | null; updated_at: string; updated_by: string }>();
    for (const row of orgAll.data?.items ?? []) {
      map.set(row.org_id, {
        rpm: row.rpm,
        tpm: row.tpm,
        updated_at: row.updated_at,
        updated_by: row.updated_by,
      });
    }
    return map;
  }, [orgAll.data]);
  const orgSkuItems = orgSkuAll.data?.items ?? [];
  const dedicatedOrgIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of orgAll.data?.items ?? []) ids.add(row.org_id);
    for (const row of orgSkuItems) ids.add(row.org_id);
    return ids;
  }, [orgAll.data, orgSkuItems]);
  const skuOverridesBySku = useMemo(() => {
    const map = new Map<string, OrgSkuRateLimitRecord[]>();
    for (const row of orgSkuItems) {
      const list = map.get(row.sku_id);
      if (list === undefined) map.set(row.sku_id, [row]);
      else list.push(row);
    }
    return map;
  }, [orgSkuItems]);

  function openOrg(nextOrg: string, focusSku?: string): void {
    setOrgOpen(false);
    setSkuOpen(false);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('org', nextOrg);
      if (focusSku !== undefined && focusSku.length > 0) next.set('sku', focusSku);
      else next.delete('sku');
      return next;
    }, { replace: true });
  }

  function closeDrawer(): void {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('org');
      next.delete('sku');
      return next;
    }, { replace: true });
  }

  function gotoSku(skuId: string): void {
    setSkuOpen(false);
    setEditorTab('sku');
    setFlashSku(skuId);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('sku', skuId);
      return next;
    }, { replace: true });
  }

  useEffect(() => {
    function onDocClick(event: MouseEvent): void {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-testid="quota-search-org"]') === null
        && target.closest('[data-testid="quota-search-org-dd"]') === null) {
        setOrgOpen(false);
      }
      if (target.closest('[data-testid="quota-search-sku"]') === null
        && target.closest('[data-testid="quota-search-sku-dd"]') === null) {
        setSkuOpen(false);
      }
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      if (!orgOpen && !skuOpen) return;
      setOrgOpen(false);
      setSkuOpen(false);
      event.preventDefault();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [orgOpen, skuOpen]);

  useEffect(() => {
    if (skuParam === null) return;
    setEditorTab('sku');
    setFlashSku(skuParam);
    const timer = window.setTimeout(() => {
      document.querySelector(`[data-testid="quota-sku-row-${skuParam}"]`)?.scrollIntoView({ block: 'center' });
    }, 50);
    const clear = window.setTimeout(() => setFlashSku(null), 1400);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clear);
    };
  }, [skuParam]);

  const orgHits = useMemo(() => {
    const q = orgQuery.trim().toLowerCase();
    return customers.filter((row) => (
      q.length === 0
      || row.org_name.toLowerCase().includes(q)
      || row.email.toLowerCase().includes(q)
      || row.org_id.toLowerCase().includes(q)
    )).slice(0, 12);
  }, [customers, orgQuery]);
  const skuHits = useMemo(() => {
    const q = skuQuery.trim().toLowerCase();
    return catalog.filter((row) => (
      q.length === 0 || row.sku_name.toLowerCase().includes(q) || row.sku_id.toLowerCase().includes(q)
    )).slice(0, 12);
  }, [catalog, skuQuery]);

  const wouldRejectCount = bypass.data?.enabled === true ? (bypass.data.would_reject_count ?? 0) : 0;

  return (
    <div className="flex flex-col gap-6" data-testid="quota-center-page">
      <PageHeader title={t('quotas.title')} sub={t('quotas.sub')} testId="quota-center-header" />

      <Panel caption={t('quotas.search.title')} padded testId="quota-search-panel">
        <div className="flex flex-wrap items-center gap-4">
          <div ref={orgAnchorRef} className="min-w-[280px] max-w-[520px] flex-1">
            <Input
              value={orgQuery}
              placeholder={t('quotas.search.org_placeholder')}
              onChange={(event) => {
                setOrgQuery(event.target.value);
                setOrgOpen(true);
                setSkuOpen(false);
              }}
              onFocus={() => {
                setOrgOpen(true);
                setSkuOpen(false);
              }}
              data-testid="quota-search-org"
            />
            <SearchDropdown open={orgOpen} anchorRef={orgAnchorRef} testId="quota-search-org-dd">
              {orgHits.length === 0 ? (
                <p className="m-0 px-3 py-2 text-dense text-paper-low">{t('quotas.search.org_empty')}</p>
              ) : orgHits.map((row) => (
                <button
                  key={row.org_id}
                  type="button"
                  role="option"
                  className="flex w-full items-baseline gap-2 px-3 py-2 text-left hover:bg-active"
                  onClick={() => {
                    setOrgQuery('');
                    openOrg(row.org_id);
                  }}
                  data-testid={`quota-search-org-${row.org_id}`}
                >
                  <span className="font-medium text-paper">{row.org_name}</span>
                  <span className="text-micro text-paper-low">{row.email}</span>
                  <span className="ml-auto font-mono text-micro text-paper-low">{truncateId(row.org_id)}</span>
                  {dedicatedOrgIds.has(row.org_id) ? (
                    <Chip tone="warn" lamp={false}>{t('quotas.search.has_override')}</Chip>
                  ) : null}
                </button>
              ))}
            </SearchDropdown>
          </div>
          <div ref={skuAnchorRef} className="min-w-[240px] max-w-[420px] flex-1">
            <Input
              value={skuQuery}
              placeholder={t('quotas.search.sku_placeholder')}
              onChange={(event) => {
                setSkuQuery(event.target.value);
                setSkuOpen(true);
                setOrgOpen(false);
              }}
              onFocus={() => {
                setSkuOpen(true);
                setOrgOpen(false);
              }}
              data-testid="quota-search-sku"
            />
            <SearchDropdown open={skuOpen} anchorRef={skuAnchorRef} testId="quota-search-sku-dd">
              {skuHits.length === 0 ? (
                <p className="m-0 px-3 py-2 text-dense text-paper-low">{t('quotas.search.sku_empty')}</p>
              ) : skuHits.map((row) => (
                <button
                  key={row.sku_id}
                  type="button"
                  role="option"
                  className="flex w-full items-baseline gap-2 px-3 py-2 text-left hover:bg-active"
                  onClick={() => {
                    setSkuQuery('');
                    gotoSku(row.sku_id);
                  }}
                  data-testid={`quota-search-sku-${row.sku_id}`}
                >
                  <span className="font-mono text-dense text-paper">{row.sku_name}</span>
                  {skuLimits.data?.items.some((item) => item.sku_id === row.sku_id) ? (
                    <Chip tone="ok" lamp={false} className="ml-auto">{t('quotas.search.has_sku_default')}</Chip>
                  ) : null}
                </button>
              ))}
            </SearchDropdown>
          </div>
          <span className="text-dense text-paper-low">{t('quotas.search.hint')}</span>
        </div>
        <p className="mb-0 mt-3 text-dense text-paper-low" data-testid="quota-search-tpm-unit">
          {t('quotas.search.tpm_unit')}
        </p>
      </Panel>

      <Panel caption={t('quotas.bypass.title')} padded testId="quota-bypass-panel">
        <p className="m-0 mb-3 text-dense leading-5 text-paper-low">{t('quotas.bypass.hint')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Switch
            checked={bypass.data?.enabled === true}
            onCheckedChange={(next) => {
              putBypass.mutate(next, {
                onSuccess: () => toast.success(t(next ? 'quotas.toast.bypass_on' : 'quotas.toast.bypass_off')),
                onError: (error) => toast.error(coerceErrorMessage(error)),
              });
            }}
            label={bypass.data?.enabled === true ? t('quotas.bypass.on') : t('quotas.bypass.off')}
            testId="quota-bypass-switch"
            disabled={putBypass.isPending}
          />
          {bypass.data?.enabled === true ? (
            <span className="text-dense text-paper-mid" data-testid="quota-bypass-meta">
              {t('quotas.bypass.by', { by: bypass.data.by ?? t('quotas.banner.unknown_operator') })}
              {' · '}
              {t('quotas.bypass.at', { at: bypass.data.at ?? '—' })}
              {' · '}
              {t('quotas.bypass.would_reject', { count: String(wouldRejectCount) })}
            </span>
          ) : null}
          <span className="ml-auto text-dense text-paper-low">{t('quotas.bypass.admin_only')}</span>
        </div>
      </Panel>

      <DedicatedCustomersPanel
        orgIds={dedicatedOrgIds}
        customerById={customerById}
        orgAllById={orgAllById}
        orgSkuItems={orgSkuItems}
        skuById={skuById}
        refusals={refusals24h.data?.items ?? []}
        filter={dedicatedFilter}
        onFilter={setDedicatedFilter}
        onOpen={openOrg}
      />

      <Panel
        caption={t('quotas.refusals.title')}
        padded={false}
        testId="quota-refusals-panel"
        actions={
          <div className="flex flex-wrap gap-2">
            {(['1h', '24h', '7d'] as const).map((window) => (
              <Button
                key={window}
                size="sm"
                variant={refusalWindow === window ? 'primary' : 'ghost'}
                onClick={() => setRefusalWindow(window)}
                data-testid={`quota-refusals-window-${window}`}
              >
                {t(`quotas.refusals.window.${window}`)}
              </Button>
            ))}
          </div>
        }
      >
        {refusals.isPending ? (
          <p className="m-0 p-4 text-dense text-paper-low">{t('quotas.refusals.loading')}</p>
        ) : (refusals.data?.items ?? []).length === 0 ? (
          <p className="m-0 p-4 text-dense text-paper-low">{t('quotas.refusals.empty')}</p>
        ) : (
          <Table testId="quota-refusals-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('quotas.refusals.org')}</Th>
                <Th>{t('quotas.refusals.sku')}</Th>
                <Th>{t('quotas.refusals.kind')}</Th>
                <Th num>{t('quotas.refusals.count')}</Th>
                <Th>{t('quotas.refusals.last')}</Th>
                <Th>{t('quotas.refusals.limit')}</Th>
                <Th />
              </Tr>
            </THead>
            <TBody>
              {(refusals.data?.items ?? []).map((row) => {
                const key = `${row.org_id ?? ''}:${row.sku_id ?? ''}:${row.kind}`;
                const org = customerLabel(row.org_id ?? '', customerById);
                const skuName = row.sku_name
                  ?? (row.sku_id === null ? '—' : (skuById.get(row.sku_id)?.sku_name ?? row.sku_id));
                return (
                  <Tr
                    key={key}
                    className="cursor-pointer"
                    onClick={() => {
                      if (row.org_id !== null) openOrg(row.org_id, row.sku_id ?? undefined);
                    }}
                    data-testid={`quota-refusal-row-${key}`}
                  >
                    <Td>
                      <span className="font-medium text-paper">{org.name}</span>
                      {org.email.length > 0 ? (
                        <div className="text-micro text-paper-low">{org.email}</div>
                      ) : null}
                    </Td>
                    <Td mono>{skuName}</Td>
                    <Td>
                      <Chip tone="bad" lamp={false}>{t(REFUSAL_KIND_KEY[row.kind])}</Chip>
                    </Td>
                    <Td num>
                      {String(row.count)}
                      {Object.keys(row.regions ?? {}).length > 0 ? (
                        <div className="font-mono text-micro text-paper-low" data-testid={`quota-refusal-regions-${key}`}>
                          {Object.entries(row.regions).map(([region, n]) => `${region} ${n}`).join(' · ')}
                        </div>
                      ) : null}
                    </Td>
                    <Td className="font-mono text-paper-low">{formatLocalTimestamp(row.last_ts)}</Td>
                    <Td>
                      {row.rpm === null && row.tpm === null
                        ? t('quotas.editor.source.none')
                        : (
                          <span className="inline-flex items-center gap-2">
                            <span className="font-mono">
                              {formatCap(row.rpm, '—')} RPM
                              {' / '}
                              {formatTpmCap(row.tpm, '—')}
                            </span>
                            <SourceChip source={row.rpm_source} />
                          </span>
                        )}
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (row.org_id !== null) openOrg(row.org_id, row.sku_id ?? undefined);
                        }}
                      >
                        {t('quotas.refusals.adjust')}
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        )}
      </Panel>

      <Panel
        caption={t('quotas.editor.title')}
        padded
        testId="quota-editor-panel"
        actions={<span className="text-dense text-paper-low">{t('quotas.editor.tab.hint')}</span>}
      >
        <Tabs
          value={editorTab}
          onValueChange={(next) => {
            if (next === 'global' || next === 'sku') setEditorTab(next);
          }}
        >
          <TabsList ariaLabel={t('quotas.editor.title')}>
            <TabsTrigger value="global" testId="quota-tab-global">{t('quotas.editor.tab.global')}</TabsTrigger>
            <TabsTrigger value="sku" testId="quota-tab-sku">{t('quotas.editor.tab.sku')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mt-4">
          {editorTab === 'global' ? (
            <GlobalEditor
              skuCount={skuLimits.data?.items.length ?? 0}
              orgCount={dedicatedOrgIds.size}
            />
          ) : (
            <SkuDefaultsTable
              catalog={catalog}
              skuLimits={skuLimits.data?.items ?? []}
              taskLaneDefaults={skuLimits.data?.task_lane_defaults ?? []}
              quotaLanes={skuLimits.data?.quota_lanes ?? []}
              covering={skuOverridesBySku}
              customerById={customerById}
              flashSku={flashSku}
              onOpenOrg={openOrg}
            />
          )}
        </div>
      </Panel>
      <p className="m-0 text-dense text-paper-low">{t('quotas.defaults.footer')}</p>

      <QuotaCustomerDrawer
        orgId={orgId}
        focusSkuId={orgId !== null ? skuParam : null}
        customers={customers}
        onClose={closeDrawer}
      />
    </div>
  );
}

function DedicatedCustomersPanel({
  orgIds,
  customerById,
  orgAllById,
  orgSkuItems,
  skuById,
  refusals,
  filter,
  onFilter,
  onOpen,
}: {
  readonly orgIds: ReadonlySet<string>;
  readonly customerById: ReadonlyMap<string, CustomerManagerCustomerSummary>;
  readonly orgAllById: ReadonlyMap<string, { rpm: number | null; tpm: number | null; updated_at: string; updated_by: string }>;
  readonly orgSkuItems: readonly OrgSkuRateLimitRecord[];
  readonly skuById: ReadonlyMap<string, ChannelModelRecord>;
  readonly refusals: readonly RateLimitRefusalRecord[];
  readonly filter: string;
  readonly onFilter: (value: string) => void;
  readonly onOpen: (orgId: string) => void;
}): JSX.Element {
  const t = useT();
  const rows = useMemo(() => {
    const skuByOrg = new Map<string, OrgSkuRateLimitRecord[]>();
    for (const row of orgSkuItems) {
      const list = skuByOrg.get(row.org_id);
      if (list === undefined) skuByOrg.set(row.org_id, [row]);
      else list.push(row);
    }
    const refusalsByOrg = new Map<string, number>();
    for (const row of refusals) {
      if (row.org_id === null) continue;
      refusalsByOrg.set(row.org_id, (refusalsByOrg.get(row.org_id) ?? 0) + row.count);
    }
    const q = filter.trim().toLowerCase();
    return [...orgIds]
      .map((orgId) => {
        const customer = customerLabel(orgId, customerById);
        const orgAll = orgAllById.get(orgId);
        const skus = skuByOrg.get(orgId) ?? [];
        let updatedAt = orgAll?.updated_at ?? '';
        let updatedBy = orgAll?.updated_by ?? '';
        for (const row of skus) {
          if (row.updated_at > updatedAt) {
            updatedAt = row.updated_at;
            updatedBy = row.updated_by;
          }
        }
        return {
          orgId,
          name: customer.name,
          email: customer.email,
          rpm: orgAll?.rpm ?? null,
          tpm: orgAll?.tpm ?? null,
          skus,
          refusals: refusalsByOrg.get(orgId) ?? 0,
          updatedAt,
          updatedBy,
        };
      })
      .filter((row) => (
        q.length === 0
        || row.name.toLowerCase().includes(q)
        || row.email.toLowerCase().includes(q)
        || row.orgId.toLowerCase().includes(q)
      ))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [orgIds, customerById, orgAllById, orgSkuItems, refusals, filter]);

  return (
    <Panel
      caption={t('quotas.dedicated.title')}
      count={rows.length}
      padded={false}
      testId="quota-dedicated-panel"
      actions={
        <Input
          wrapperClassName="w-56"
          value={filter}
          placeholder={t('quotas.dedicated.search')}
          onChange={(event) => onFilter(event.target.value)}
          data-testid="quota-dedicated-filter"
        />
      }
    >
      {orgIds.size === 0 ? (
        <p className="m-0 p-4 text-dense text-paper-low">{t('quotas.dedicated.empty')}</p>
      ) : rows.length === 0 ? (
        <p className="m-0 p-4 text-dense text-paper-low" data-testid="quota-dedicated-none-match">
          {t('quotas.dedicated.none_match')}
        </p>
      ) : (
        <Table testId="quota-dedicated-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('quotas.dedicated.org')}</Th>
              <Th>{t('quotas.dedicated.org_all')}</Th>
              <Th>{t('quotas.dedicated.sku_count')}</Th>
              <Th num>{t('quotas.dedicated.refusals_24h')}</Th>
              <Th>{t('quotas.dedicated.updated')}</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => {
              const names = row.skus
                .map((item) => {
                  const name = skuById.get(item.sku_id)?.sku_name ?? item.sku_id;
                  return skuShortName(name);
                })
                .sort((a, b) => a.localeCompare(b));
              const shown = names.slice(0, 2);
              const extra = names.length - shown.length;
              return (
                <Tr
                  key={row.orgId}
                  className="cursor-pointer"
                  onClick={() => onOpen(row.orgId)}
                  data-testid={`quota-dedicated-row-${row.orgId}`}
                >
                  <Td>
                    <span className="font-medium text-paper">{row.name}</span>
                    {row.email.length > 0 ? (
                      <div className="text-micro text-paper-low">{row.email}</div>
                    ) : null}
                  </Td>
                  <Td mono>
                    {row.rpm === null && row.tpm === null
                      ? '—'
                      : `${row.rpm ?? '—'} / ${formatTpmCap(row.tpm, '—')}`}
                  </Td>
                  <Td>
                    <span className="font-mono">{row.skus.length}</span>
                    {shown.length > 0 ? (
                      <span className="ml-2 text-micro text-paper-low">
                        {shown.join(', ')}
                        {extra > 0 ? ` ${t('quotas.dedicated.more', { n: String(extra) })}` : ''}
                      </span>
                    ) : null}
                  </Td>
                  <Td num>{row.refusals}</Td>
                  <Td className="font-mono text-paper-low">
                    {row.updatedAt.length > 0 ? `${formatLocalTimestamp(row.updatedAt)} · ${row.updatedBy}` : '—'}
                  </Td>
                  <Td>
                    <Button size="sm" variant="ghost">{t('quotas.dedicated.open')}</Button>
                  </Td>
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}

function GlobalEditor({
  skuCount,
  orgCount,
}: {
  readonly skuCount: number;
  readonly orgCount: number;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const row = useGlobalRateLimit();
  const put = usePutGlobalRateLimit();
  const clear = useDeleteGlobalRateLimit();
  const [rpm, setRpm] = useState('');
  const [tpm, setTpm] = useState('');
  const item = row.data?.item;
  useEffect(() => {
    if (item === undefined) return;
    setRpm(item === null || item.rpm === null ? '' : String(item.rpm));
    setTpm(item === null || item.tpm === null ? '' : formatTpmMInput(item.tpm));
  }, [item]);
  return (
    <div className="flex flex-col gap-3" data-testid="quota-global-editor">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <p className="m-0 text-dense text-paper">{t('quotas.editor.global_audience')}</p>
          <p className="mb-0 mt-0.5 text-micro text-paper-low">{t('quotas.editor.global_audience_hint')}</p>
        </div>
        <span className="text-dense text-paper-mid">{t('quotas.editor.rpm')}</span>
        <Input
          mono
          wrapperClassName="w-24"
          value={rpm}
          onChange={(event) => setRpm(event.target.value)}
          data-testid="quota-global-rpm"
        />
        <span className="text-dense text-paper-mid">{t('quotas.editor.tpm')}</span>
        <TpmField
          wrapperClassName="w-28"
          value={tpm}
          onChange={(event) => setTpm(event.target.value)}
          data-testid="quota-global-tpm"
        />
        <Button
          variant="primary"
          onClick={() => {
            const body = parseCaps(rpm, tpm, item === null || item === undefined ? null : item.tpm);
            if (body === null) {
              toast.error(t('quotas.toast.invalid'));
              return;
            }
            put.mutate(body, {
              onSuccess: () => toast.success(t('quotas.toast.saved')),
              onError: (error) => toast.error(coerceErrorMessage(error)),
            });
          }}
          data-testid="quota-global-save"
        >
          {t('quotas.editor.save')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            clear.mutate(undefined, {
              onSuccess: () => toast.success(t('quotas.toast.cleared')),
              onError: (error) => toast.error(coerceErrorMessage(error)),
            });
          }}
          data-testid="quota-global-clear"
        >
          {t('quotas.editor.clear')}
        </Button>
        <span className="text-dense text-paper-low">
          {t('quotas.editor.cover_counts', { skus: String(skuCount), orgs: String(orgCount) })}
        </span>
      </div>
    </div>
  );
}

function skuQuotaLane(
  skuId: string,
  taskLaneById: ReadonlyMap<string, TaskLaneSkuDefault>,
  quotaLaneById: ReadonlyMap<string, QuotaLane>,
): QuotaLane {
  return quotaLaneById.get(skuId)
    ?? (taskLaneById.has(skuId) ? 'task' : 'llm');
}

function SkuDefaultsTable({
  catalog,
  skuLimits,
  taskLaneDefaults,
  quotaLanes,
  covering,
  customerById,
  flashSku,
  onOpenOrg,
}: {
  readonly catalog: readonly ChannelModelRecord[];
  readonly skuLimits: readonly { sku_id: string; rpm: number | null; tpm: number | null }[];
  readonly taskLaneDefaults: readonly TaskLaneSkuDefault[];
  readonly quotaLanes: readonly SkuQuotaLane[];
  readonly covering: ReadonlyMap<string, readonly OrgSkuRateLimitRecord[]>;
  readonly customerById: ReadonlyMap<string, CustomerManagerCustomerSummary>;
  readonly flashSku: string | null;
  readonly onOpenOrg: (orgId: string) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const put = usePutSkuRateLimit();
  const clear = useDeleteSkuRateLimit();
  const limitById = useMemo(() => {
    const map = new Map<string, { rpm: number | null; tpm: number | null }>();
    for (const item of skuLimits) map.set(item.sku_id, item);
    return map;
  }, [skuLimits]);
  const taskLaneById = useMemo(() => {
    const map = new Map<string, TaskLaneSkuDefault>();
    for (const item of taskLaneDefaults) map.set(item.sku_id, item);
    return map;
  }, [taskLaneDefaults]);
  const quotaLaneById = useMemo(() => {
    const map = new Map<string, QuotaLane>();
    for (const item of quotaLanes) map.set(item.sku_id, item.quota_lane);
    return map;
  }, [quotaLanes]);
  const [drafts, setDrafts] = useState<Readonly<Record<string, { rpm: string; tpm: string }>>>({});

  const groups = groupByNamespace(catalog, (sku) => sku.sku_name);
  return (
    <Table testId="quota-sku-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('quotas.drawer.sku')}</Th>
          <Th num>{t('quotas.editor.rpm')}</Th>
          <Th num>{t('quotas.editor.tpm')}</Th>
          <Th>{t('quotas.editor.covering')}</Th>
          <Th />
        </Tr>
      </THead>
      <TBody>
        {groups.map((group) => {
          const overrideCount = group.items.filter((sku) => limitById.has(sku.sku_id)).length;
          return (
            <FragmentGroup
              key={group.namespace}
              namespace={group.namespace}
              count={group.items.length}
              overrides={overrideCount}
            >
              {group.items.map((sku) => {
                const limit = limitById.get(sku.sku_id);
                const draft = drafts[sku.sku_id] ?? {
                  rpm: typeof limit?.rpm === 'number' ? String(limit.rpm) : '',
                  tpm: formatTpmMInput(limit?.tpm),
                };
                const overlays = covering.get(sku.sku_id) ?? [];
                const taskLane = taskLaneById.get(sku.sku_id);
                const quotaLane = skuQuotaLane(sku.sku_id, taskLaneById, quotaLaneById);
                const leftoverTpm = typeof limit?.tpm === 'number';
                return (
                  <Tr
                    key={sku.sku_id}
                    interactive={false}
                    data-testid={`quota-sku-row-${sku.sku_id}`}
                    className={flashSku === sku.sku_id ? 'bg-signal-dim' : undefined}
                  >
                    <Td mono>
                      <span title={sku.sku_name}>{skuShortName(sku.sku_name)}</span>
                    </Td>
                    {taskLane !== undefined || quotaLane === 'task' ? (
                      <Td colSpan={2} data-testid={`quota-sku-task-lane-${sku.sku_id}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          {Object.entries(taskLane?.default_max_in_flight_by_tier_class ?? {}).map(([tier, allowance]) => (
                            <Chip
                              key={tier}
                              tone="neutral"
                              lamp={false}
                              testId={`quota-sku-task-lane-chip-${sku.sku_id}-${tier}`}
                            >
                              {`${tier}: ${String(allowance)}`}
                            </Chip>
                          ))}
                          {taskLane !== undefined ? (
                            <Link
                              to={`/supply/models/${encodeURIComponent(taskLane.vendor)}/${encodeURIComponent(taskLane.model)}`}
                              className="text-dense text-signal hover:text-signal-bright"
                              data-testid={`quota-sku-task-lane-protocol-${sku.sku_id}`}
                            >
                              {t('quotas.task_lane.edit_protocol')}
                            </Link>
                          ) : null}
                          {limit !== undefined ? (
                            <>
                              <Chip tone="warn" lamp={false} testId={`quota-sku-task-lane-warn-${sku.sku_id}`}>
                                {t('quotas.task_lane.ignores_rpm')}
                              </Chip>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  clear.mutate(
                                    { skuId: sku.sku_id },
                                    {
                                      onSuccess: () => toast.success(t('quotas.toast.cleared')),
                                      onError: (error) => toast.error(coerceErrorMessage(error)),
                                    },
                                  );
                                }}
                                data-testid={`quota-sku-clear-leftover-${sku.sku_id}`}
                              >
                                {t('quotas.task_lane.clear_leftover')}
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </Td>
                    ) : (
                      <>
                        <Td num>
                          <Input
                            mono
                            wrapperClassName="w-24"
                            value={draft.rpm}
                            placeholder={t('quotas.editor.inherit')}
                            className={draft.rpm.length === 0 ? 'italic' : undefined}
                            onChange={(event) => {
                              const rpm = event.target.value;
                              setDrafts((prev) => ({ ...prev, [sku.sku_id]: { rpm, tpm: (prev[sku.sku_id] ?? draft).tpm } }));
                            }}
                            data-testid={`quota-sku-rpm-${sku.sku_id}`}
                          />
                        </Td>
                        {quotaLane === 'image' ? (
                          <Td data-testid={`quota-sku-image-lane-${sku.sku_id}`}>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-dense text-paper-low">{t('quotas.image_lane.rpm_only')}</span>
                              {leftoverTpm ? (
                                <>
                                  <Chip tone="warn" lamp={false} testId={`quota-sku-image-lane-warn-${sku.sku_id}`}>
                                    {t('quotas.image_lane.ignores_tpm')}
                                  </Chip>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      if (typeof limit?.rpm === 'number') {
                                        put.mutate(
                                          { skuId: sku.sku_id, body: { rpm: limit.rpm, tpm: null } },
                                          {
                                            onSuccess: () => toast.success(t('quotas.toast.cleared')),
                                            onError: (error) => toast.error(coerceErrorMessage(error)),
                                          },
                                        );
                                      } else {
                                        clear.mutate(
                                          { skuId: sku.sku_id },
                                          {
                                            onSuccess: () => toast.success(t('quotas.toast.cleared')),
                                            onError: (error) => toast.error(coerceErrorMessage(error)),
                                          },
                                        );
                                      }
                                    }}
                                    data-testid={`quota-sku-clear-leftover-${sku.sku_id}`}
                                  >
                                    {t('quotas.task_lane.clear_leftover')}
                                  </Button>
                                </>
                              ) : null}
                            </div>
                          </Td>
                        ) : (
                          <Td num>
                            <TpmField
                              wrapperClassName="w-24"
                              value={draft.tpm}
                              placeholder={t('quotas.editor.inherit')}
                              className={draft.tpm.length === 0 ? 'italic' : undefined}
                              onChange={(event) => {
                                const tpm = event.target.value;
                                setDrafts((prev) => ({ ...prev, [sku.sku_id]: { rpm: (prev[sku.sku_id] ?? draft).rpm, tpm } }));
                              }}
                              data-testid={`quota-sku-tpm-${sku.sku_id}`}
                            />
                          </Td>
                        )}
                      </>
                    )}
                    <Td>
                      {overlays.length === 0 ? (
                        <span className="text-paper-low">{t('quotas.editor.covering_none')}</span>
                      ) : overlays.map((overlay, index) => {
                        const label = customerById.get(overlay.org_id)?.org_name ?? overlay.org_id;
                        return (
                          <span key={overlay.org_id}>
                            {index > 0 ? '、' : null}
                            <button
                              type="button"
                              className="text-signal hover:text-signal-bright"
                              onClick={() => onOpenOrg(overlay.org_id)}
                              data-testid={`quota-sku-cover-${sku.sku_id}-${overlay.org_id}`}
                            >
                              {label}
                            </button>
                          </span>
                        );
                      })}
                    </Td>
                    <Td>
                      {taskLane === undefined ? (
                        <Button
                          size="sm"
                          onClick={() => {
                            const write = buildRateLimitWrite(draft.rpm, draft.tpm, limit?.tpm ?? null);
                            if (write.kind === 'invalid') {
                              toast.error(t('quotas.toast.invalid'));
                              return;
                            }
                            if (write.kind === 'delete') {
                              clear.mutate(
                                { skuId: sku.sku_id },
                                {
                                  onSuccess: () => toast.success(t('quotas.toast.cleared')),
                                  onError: (error) => toast.error(coerceErrorMessage(error)),
                                },
                              );
                              return;
                            }
                            put.mutate(
                              { skuId: sku.sku_id, body: write.body },
                              {
                                onSuccess: () => toast.success(t('quotas.toast.saved')),
                                onError: (error) => toast.error(coerceErrorMessage(error)),
                              },
                            );
                          }}
                          data-testid={`quota-sku-save-${sku.sku_id}`}
                        >
                          {t('quotas.editor.save')}
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                );
              })}
            </FragmentGroup>
          );
        })}
      </TBody>
    </Table>
  );
}

function FragmentGroup({
  namespace,
  count,
  overrides,
  children,
}: {
  readonly namespace: string;
  readonly count: number;
  readonly overrides: number;
  readonly children: ReactNode;
}): JSX.Element {
  const t = useT();
  return (
    <>
      <Tr interactive={false} data-testid={`quota-sku-group-${namespace}`}>
        <Td colSpan={5} className="bg-canvas text-label text-paper-low">
          {namespace}
          {' · '}
          {t('quotas.editor.group_meta', { count: String(count), overrides: String(overrides) })}
        </Td>
      </Tr>
      {children}
    </>
  );
}
